import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  merge,
  shareReplay,
  type Observable,
} from 'rxjs';
import { ViewIds } from '@ardoq/api-types';
import { shallowEqual } from 'utils/objectUtils';
import { DEFAULT_DEBOUNCE_TIME } from 'modelInterface/consts';
import { toViewpointModeMainViewInstanceId } from 'appLayout/mainViewInstanceIds';
import { getViewpointModeViewSettingsWithChanges$ } from 'views/environmentals/viewSettings/viewpointModeViewSettings$';
import { warmConditionalFormattingFilterCache } from 'tabview/bubbleChart/sourceData/buildViewpointModeViewModelSourceData';
import { buildSurfaceSourceData } from '../sourceData/buildSurfaceSourceData';
import type { Surface3DViewProps } from '../Surface3DView';
import {
  toPlottedViewEffects$,
  toStableSelection$,
} from 'tabview/chartShared/plottedViewEffects';
import { XYZ_DERIVATION_INPUTS, toXyzFieldSelection } from './xyzDerivation';
import { getViewLegendLabels } from 'viewLegend/getViewLegendLabels';
import { toUnknownPaintCss } from '@ardoq/xyz-charts';
import { toFieldColoringLegendRows } from '../fieldColoringLegend';
import { SURFACE_3D_COLOR_TOKENS } from '../surface3dColorTokens';

const VIEW_ID = ViewIds.SURFACE_3D;
const VIEW_INSTANCE_ID = toViewpointModeMainViewInstanceId(VIEW_ID);

const viewSettings$ = getViewpointModeViewSettingsWithChanges$(
  VIEW_ID,
  VIEW_INSTANCE_ID
).pipe(map(({ currentSettings }) => currentSettings));

const sourceData$ = combineLatest({
  ...XYZ_DERIVATION_INPUTS,
  /* The five field names, plus the one draw setting that is also a gridding input:
     `interpolation` decides which kernel fills the grid, and it is what the builder's
     'no area to interpolate' check reads. Everything else the renderer reads - the ramp, the
     bands, the mesh flags - is kept out, and out of the type, so the builder cannot read it. */
  viewSettings: toStableSelection$(viewSettings$, settings => ({
    ...toXyzFieldSelection(settings),
    interpolation: settings.interpolation,
    binCount: settings.binCount,
  })),
}).pipe(
  debounceTime(DEFAULT_DEBOUNCE_TIME),
  distinctUntilChanged(shallowEqual),
  map(args =>
    buildSurfaceSourceData(VIEW_INSTANCE_ID, {
      ...args,
      /* Warmed in the same step that reads it; see `contourViewModel$`. The surface's sample
         markers take a component's conditional-formatting colour, so an unwarmed read shows
         whatever the previously rendered view left in the singleton. */
      filterColorData: warmConditionalFormattingFilterCache(args.loadedGraph),
    })
  ),
  shareReplay({ bufferSize: 1, refCount: true })
);

/** Everything the 3D surface view is rendered from. */
export const surface3dViewModel$: Observable<Surface3DViewProps> = merge(
  combineLatest([sourceData$, viewSettings$]).pipe(
    map(([sourceData, viewSettings]) => ({
      ...sourceData,
      viewSettings,
      legendRows: toFieldColoringLegendRows(
        {
          coloring: sourceData.coloring,
          ...sourceData.legend,
          zAxisTitle: sourceData.zAxisTitle,
          ramp: viewSettings.ramp,
          bands: viewSettings.bands,
          unknownColor: toUnknownPaintCss(SURFACE_3D_COLOR_TOKENS.surface),
        },
        sourceData.i18nLocale
      ),
      viewLegendLabels: getViewLegendLabels(sourceData.i18nLocale),
    }))
  ),
  toPlottedViewEffects$(sourceData$, VIEW_ID)
);
